import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MarkdownModule } from 'ngx-markdown';
import { MatExpansionModule } from '@angular/material/expansion';
import { v4 as uuidv4 } from 'uuid';

import Resources from '@constants/resourses';
import { selectedAddressModel } from 'src/app/core/models/selectedAddress.interface';
import { CurrentUsageModel } from 'src/app/core/models/currentUsage.interface';
import { ISupplierDetails, ISupplierList, ISupplierTariff } from 'src/app/core/models/quotesTariff.interface';
import { AlertDialogData } from '@shared/dialog/alert-dialog/alert-dialog.interface';

import { PostcodeLookupComponent } from '../postcode-lookup/postcode-lookup.component';
import { LoginComponent } from '@shared/dialog/login/login.component';
import { AlertDialogComponent } from '@shared/dialog/alert-dialog/alert-dialog.component';
import { ConfirmDialogComponent } from '@shared/dialog/confirm-dialog/confirm-dialog.component';

import { UtilityService } from '@services/utility.service';
import { SessionService } from '@services/session.service';
import { TealiumUtagService } from '@services/utag.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { RestapiService } from '@services/restapi.service';
import { AppService } from '@services/app.service';
import { SocketService } from '@services/socket.service';
import { ABTestingService } from '@services/abtesting.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MarkdownModule, MatFormFieldModule, MatInputModule, MatSelectModule, PostcodeLookupComponent, MatDialogModule, MatCheckboxModule, MatRadioModule, MatExpansionModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  currentDetailsFormGroup!: FormGroup;
  contactDetailsFormGroup!: FormGroup;
  isLoggedIn: boolean = this.utilityService.isLoggedIn();
  navigateToURL = (page: string) => this.utilityService?.navigateTo(page);

  supplierConfig: any;
  regex: any;

  selectedAddress!: selectedAddressModel | undefined;
  currentUsage!: CurrentUsageModel;
  correlationId: string = '';
  savedDetails: any = {};
  isReset: boolean = false;

  id: string = 'EnergyComparison_PostcodeEntry';
  // tealiumData: any = {};
  postcode: string = '';
  email: string = '';
  isExceptionalFlow: boolean = false;
  abVariant: string;
  ctmFlowVariant: string;
  googleAnalytics = inject(GoogleAnalyticsService);

  isSupplierLoading: boolean = false;
  isCurrentUsageLoading: boolean = false;

  fuelType: string = '';
  listOfSuppliers!: ISupplierList;

  bothSupplierList: ISupplierDetails[] = [];
  electricitySupplierList: ISupplierDetails[] = [];
  gasSupplierList: ISupplierDetails[] = [];

  bothTariffList: ISupplierTariff[] = [];
  electricityTariffList: ISupplierTariff[] = [];
  gasTariffList: ISupplierTariff[] = [];

  paymentList: { id: number, name: string }[] = [];
  electricityPaymentList: { id: number, name: string }[] = [];
  gasPaymentList: { id: number, name: string }[] = [];

  marketingConsentList: any[] = [];
  selectedConsent: any[] = [];
  CtmMarketingConsent: string;

  queryParams: any;
  isFooterSticky: boolean = true;
  isAtBottom: boolean = false;

  isSubmitting: boolean = false;
  calculatedSpend: any;

  personalDetails: any;
  isConsumptionSpendMissing: boolean = false;
  openPopup = (content: any, title?: string, buttonText?: string) => {
    const alertDialogData: AlertDialogData = {
      pageName: 'Filter',
      title: title || 'Sorry, something went wrong',
      message: content,
      buttonText: buttonText || 'Close'
    };

    this.dialog.open(AlertDialogComponent, {
      data: alertDialogData,
      maxWidth: window.innerWidth <= 767 ? '95vw' : '600px',
      disableClose: false,
      autoFocus: false,
      panelClass: 'ctm-modal'
    });
  }

  openDialog = () => {
    const alertDialogData: AlertDialogData = {
      pageName: 'PaymentContent',
      title: 'Not sure which one to choose?',
      message: `
        <div class="payment-info">
          <h3 class="fs-17 mb-2 fw700">Monthly direct debit</h3>
          <p class="fs-17 mb-3">Choose this if the money comes out of your bank account every month.</p>
          
          <h3 class="fs-17 mb-2 fw700">Quarterly direct debit</h3>
          <p class="fs-17 mb-3">Choose this if the money comes out of your bank account every three months.</p>
          
          <h3 class="fs-17 mb-2 fw700">Pay when you get the bill</h3>
          <p class="fs-17 mb-3">Choose this if you get a bill and pay it when it arrives.</p>
          
          <h3 class="fs-17 mb-2 fw700">Prepayment meter</h3>
          <p class="fs-17 mb-3">Choose if you have a meter at home and pay using a card, key or token.</p>
          
          <h3 class="fs-17 mb-2 fw700">I'm not sure</h3>
          <p class="fs-17 mb-3">Choose this if you don't know how you pay.</p>
        </div>
      `,
      buttonText: 'Close'
    }

    this.dialog.open(AlertDialogComponent, {
      data: alertDialogData,
      maxWidth: window.innerWidth <= 767 ? '95vw' : '600px',
      disableClose: false,
      autoFocus: false,
      panelClass: 'ctm-modal'
    });
  }

  constructor(
    private utilityService: UtilityService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private sessionService: SessionService,
    private tealiumService: TealiumUtagService,
    private restAPI: RestapiService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private appService: AppService,
    private socketService: SocketService,
    private abTestingService: ABTestingService
  ) {
    this.email = this.sessionService.getData(Resources.SessionStorageKeys.Email);
    this.CtmMarketingConsent = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.MarketingConsentChannels);
    this.isLoggedIn = this.utilityService.isLoggedIn();

    if (this.isLoggedIn) {
      this.personalDetails = this.sessionService.getData(Resources.SessionStorageKeys.PersonalDetails);
      this.savedDetails = this.sessionService.getData(Resources.SessionStorageKeys.SavedDetail);
    }

    this.subscriptions.push(
      this.utilityService.getDefaultSupplierConfiguration().subscribe({
        next: (res) => {
          this.regex = res?.data?.Generic?.Postcode?.pattern;
          this.supplierConfig = res?.data;

          // If visible then show the CTM Marketing Consent and email field
          const marketingConsent = res?.data?.CTMMarketingConsent;
          if (marketingConsent?.isVisible) {

            // CTM Marketing Consent
            this.marketingConsentList = (this.supplierConfig?.CTMMarketingConsent?.data?.communicationChannels).map((item: any, i: number) => {
              return {
                id: i + 1,
                name: item,
                isChecked: false
              };
            });

            // If already given consent then no need to show the consent field and email field, instead directly store the consent in session storage and use it in the flow.
            // Initialize contact details form group
            this.contactDetailsFormGroup = this.fb.group({
              email: ['', [Validators.required, Validators.pattern(res?.data?.Generic?.Email?.pattern)]],
              consent: ['', [Validators.required]]
            });
          }
        },
        error: (err) => {
          console.error('Error fetching configuration:', err);
        }
      })
    );

    // Resolve and cookie the top spotlight A/B variant as early as possible, reusing the
    // same (cached) supplier config call above rather than waiting until the quote page
    this.subscriptions.push(
      this.abTestingService.getTopSpotlightVariant().subscribe()
    );

    // Resetting the session storage
    this.sessionService.removeData(Resources.SessionStorageKeys.QuotesData);
    this.postcode = '';
  }

  // Host listener to detect scroll events
  // This will be used to determine if the footer should be sticky or not
  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Get the distance from the bottom of the page
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const distanceToBottom = documentHeight - (scrollTop + windowHeight);

    // Check if we're near the bottom of the page (within 100px)
    this.isAtBottom = distanceToBottom < 100;

    // Footer should be sticky except when at the bottom of the page
    this.isFooterSticky = !this.isAtBottom;

    // No need to modify body classes for margin now that we're using position: sticky
  }

  ngOnInit(): void {
    // Tealium view event
    this.tealiumService.view({
      page_name: 'Postcode Entry',
      page_type: 'Risk Journey',
      // Logged in customers must have customer_id set from the start of the journey
      customer_id: this.tealiumService.hashEmail(this.sessionService.getData(Resources.SessionStorageKeys.Email))
    });

    // Check if campaign ID is present in the url
    if (this.activatedRoute.snapshot.queryParams?.campaignId) {
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CampaignId, this.activatedRoute.snapshot.queryParams.campaignId);
    }

    // Query params
    if (this.activatedRoute.snapshot.queryParams) {
      this.queryParams = { ...this.activatedRoute.snapshot.queryParams };
    }

    // Google Analytics event
    this.googleAnalytics.track({
      page: 'Postcode'
    }, 'page_view_postcode');

    // Initial check for footer position
    setTimeout(() => {
      this.onWindowScroll();
    }, 100);

    // Initialise current details form group
    this.currentDetailsFormGroup = this.fb.group({
      fuelType: ['', [Validators.required]],
      isSameSupplier: [''],
      supplierName: [''],
      paymentMethod: [''],
      supplierTariff: [''],
      electricitySupplierName: [''],
      electricityPaymentMethod: [''],
      electricitySupplierTariff: [''],
      gasSupplierName: [''],
      gasPaymentMethod: [''],
      gasSupplierTariff: [''],
      energyProfile: ['energyUsage', [Validators.required]],
      duration: ['annual', [Validators.required]],
      electricityConsumption: [''],
      electricitySpend: [''],
      gasConsumption: [''],
      gasSpend: [''],
    });

    // Fuel type change
    this.currentDetailsFormGroup?.get('fuelType')?.valueChanges.subscribe(value => {
      this.fuelType = value;

      // Reset supplier and tariff when fuel type change
      if (this.fuelType === Resources.FuelType.BOTH) {
        this.currentDetailsFormGroup.get('isSameSupplier')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('isSameSupplier')?.setValue('');
        this.currentDetailsFormGroup.get('supplierName')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('supplierName')?.setValue('');
        this.currentDetailsFormGroup.get('electricitySupplierName')?.clearValidators();
        this.currentDetailsFormGroup.get('electricitySupplierName')?.setValue('');
        this.currentDetailsFormGroup.get('gasSupplierName')?.clearValidators();
        this.currentDetailsFormGroup.get('gasSupplierName')?.setValue('');
        this.currentDetailsFormGroup.get('energyProfile')?.setValue('energyUsage');
        this.currentDetailsFormGroup.get('duration')?.setValue('annual');

        this.currentDetailsFormGroup.get('isSameSupplier')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('supplierName')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('electricitySupplierName')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('gasSupplierName')?.updateValueAndValidity();
      } else if (this.fuelType === Resources.FuelType.ELECTRICITY) {
        this.currentDetailsFormGroup.get('electricitySupplierName')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('electricitySupplierName')?.setValue('');
        this.currentDetailsFormGroup.get('energyProfile')?.setValue('energyUsage');
        this.currentDetailsFormGroup.get('duration')?.setValue('annual');

        this.currentDetailsFormGroup.get('isSameSupplier')?.clearValidators();
        this.currentDetailsFormGroup.get('isSameSupplier')?.setValue('');

        this.currentDetailsFormGroup.get('supplierName')?.clearValidators();
        this.currentDetailsFormGroup.get('supplierName')?.setValue('');
        this.currentDetailsFormGroup.get('gasSupplierName')?.clearValidators();
        this.currentDetailsFormGroup.get('gasSupplierName')?.setValue('');

        this.currentDetailsFormGroup.get('electricitySupplierName')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('isSameSupplier')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('supplierName')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('gasSupplierName')?.updateValueAndValidity();
      } else if (this.fuelType === Resources.FuelType.GAS) {
        this.currentDetailsFormGroup.get('gasSupplierName')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('gasSupplierName')?.setValue('');

        this.currentDetailsFormGroup.get('isSameSupplier')?.clearValidators();
        this.currentDetailsFormGroup.get('isSameSupplier')?.setValue('');
        this.currentDetailsFormGroup.get('supplierName')?.clearValidators();
        this.currentDetailsFormGroup.get('supplierName')?.setValue('');
        this.currentDetailsFormGroup.get('electricitySupplierName')?.clearValidators();
        this.currentDetailsFormGroup.get('electricitySupplierName')?.setValue('');
        this.currentDetailsFormGroup.get('energyProfile')?.setValue('energyUsage');
        this.currentDetailsFormGroup.get('duration')?.setValue('annual');

        this.currentDetailsFormGroup.get('gasSupplierName')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('isSameSupplier')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('supplierName')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('electricitySupplierName')?.updateValueAndValidity();
      }

      // Update energy field validators when fuel type changes
      this.updateEnergyFieldValidators();
    });

    // Is same supplier change - For both fuel type only
    this.currentDetailsFormGroup?.get('isSameSupplier')?.valueChanges.subscribe(value => {
      if (value === 'Yes') {
        this.currentDetailsFormGroup.get('supplierName')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('supplierName')?.updateValueAndValidity();

        this.currentDetailsFormGroup.get('electricitySupplierName')?.clearValidators();
        this.currentDetailsFormGroup.get('gasSupplierName')?.clearValidators();

        this.currentDetailsFormGroup.get('electricitySupplierName')?.setValue('');
        this.currentDetailsFormGroup.get('gasSupplierName')?.setValue('');

        this.currentDetailsFormGroup.get('energyProfile')?.setValue('energyUsage');
        this.currentDetailsFormGroup.get('duration')?.setValue('annual');

        this.currentDetailsFormGroup.get('electricitySupplierName')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('gasSupplierName')?.updateValueAndValidity();
      } else if (value === 'No') {
        this.currentDetailsFormGroup.get('electricitySupplierName')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('gasSupplierName')?.setValidators(Validators.required);

        this.currentDetailsFormGroup.get('supplierName')?.clearValidators();
        this.currentDetailsFormGroup.get('supplierName')?.setValue('');
        this.currentDetailsFormGroup.get('energyProfile')?.setValue('energyUsage');
        this.currentDetailsFormGroup.get('duration')?.setValue('annual');

        this.currentDetailsFormGroup.get('supplierName')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('electricitySupplierName')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('gasSupplierName')?.updateValueAndValidity();
      }
    });

    // Supplier change - for both fuel type only
    this.currentDetailsFormGroup?.get('supplierName')?.valueChanges.subscribe(value => {
      if (value) {
        this.currentDetailsFormGroup.get('paymentMethod')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('paymentMethod')?.updateValueAndValidity();

        this.currentDetailsFormGroup.get('electricityPaymentMethod')?.clearValidators();
        this.currentDetailsFormGroup.get('electricityPaymentMethod')?.setValue('');
        this.currentDetailsFormGroup.get('electricityPaymentMethod')?.updateValueAndValidity();
        this.currentDetailsFormGroup.get('gasPaymentMethod')?.clearValidators();
        this.currentDetailsFormGroup.get('gasPaymentMethod')?.setValue('');
        this.currentDetailsFormGroup.get('gasPaymentMethod')?.updateValueAndValidity();

        // Get payment method list for both fuel types based on selected supplier
        this.getPaymentList(Resources.FuelType.BOTH, value);
      }
    });

    // Supplier change - for electricity fuel type only
    this.currentDetailsFormGroup?.get('electricitySupplierName')?.valueChanges.subscribe(value => {
      if (value) {
        this.currentDetailsFormGroup.get('electricityPaymentMethod')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('electricityPaymentMethod')?.updateValueAndValidity();

        this.currentDetailsFormGroup.get('paymentMethod')?.clearValidators();
        this.currentDetailsFormGroup.get('paymentMethod')?.setValue('');
        this.currentDetailsFormGroup.get('paymentMethod')?.updateValueAndValidity();

        // Get electricity payment method list based on selected supplier
        this.getPaymentList(Resources.FuelType.ELECTRICITY, value);
      }
    });

    // Supplier change - for gas fuel type only
    this.currentDetailsFormGroup?.get('gasSupplierName')?.valueChanges.subscribe(value => {
      if (value) {
        this.currentDetailsFormGroup.get('gasPaymentMethod')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('gasPaymentMethod')?.updateValueAndValidity();

        this.currentDetailsFormGroup.get('paymentMethod')?.clearValidators();
        this.currentDetailsFormGroup.get('paymentMethod')?.setValue('');
        this.currentDetailsFormGroup.get('paymentMethod')?.updateValueAndValidity();

        // Get gas payment method list based on selected supplier
        this.getPaymentList(Resources.FuelType.GAS, value);
      }
    });

    // Payment method change - for both fuel type only
    this.currentDetailsFormGroup?.get('paymentMethod')?.valueChanges.subscribe(value => {
      if (value) {
        // get tariff list based on fuelType, paymentMethod and Supplier
        this.getTariffList(this.fuelType, value, this.currentDetailsFormGroup?.get('supplierName')?.value);
      }
    });

    // Electricity payment method change
    this.currentDetailsFormGroup?.get('electricityPaymentMethod')?.valueChanges.subscribe(value => {
      if (value) {
        // get tariff list based on fuelType, paymentMethod and Supplier
        this.getTariffList('Electricity', value, this.currentDetailsFormGroup?.get('electricitySupplierName')?.value);
      }
    });

    // Gas payment method change
    this.currentDetailsFormGroup?.get('gasPaymentMethod')?.valueChanges.subscribe(value => {
      if (value) {
        // get tariff list based on fuelType, paymentMethod and Supplier
        this.getTariffList('Gas', value, this.currentDetailsFormGroup?.get('gasSupplierName')?.value);
      }
    });

    // Energy profile change
    this.currentDetailsFormGroup?.get('energyProfile')?.valueChanges.subscribe(value => {
      if (!value) {
        this.currentDetailsFormGroup.get('supplierTariff')?.setValue('');
        this.currentDetailsFormGroup.get('electricitySupplierTariff')?.setValue('');
        this.currentDetailsFormGroup.get('gasSupplierTariff')?.setValue('');
      }
    });

    // Energy profile change - Current Spend or Energy Usage
    this.currentDetailsFormGroup?.get('energyProfile')?.valueChanges.subscribe(value => {
      // Reset duration when energy profile changes
      this.currentDetailsFormGroup.get('duration')?.setValue('monthly');

      // Update validators based on energy profile
      if (!!this.isConsumptionSpendMissing) {
        this.updateEnergyFieldValidators();
      }
    });

    // Duration change - Monthly or Annual
    this.currentDetailsFormGroup?.get('duration')?.valueChanges.subscribe(value => {
      // Update validators when duration changes
      if (!!this.isConsumptionSpendMissing) {
        this.updateEnergyFieldValidators();
      }
    });
  }

  // Update energy field validators based on energyProfile and duration
  updateEnergyFieldValidators(): void {
    const fuelType = this.currentDetailsFormGroup?.get('fuelType')?.value;
    const energyProfile = this.currentDetailsFormGroup?.get('energyProfile')?.value;
    // Current Spend
    if (energyProfile === 'currentSpend') {
      // For Both and Electricity
      if (fuelType === Resources.FuelType.BOTH || fuelType === Resources.FuelType.ELECTRICITY) {
        // Current Spend selected - set validators for spend fields
        this.currentDetailsFormGroup.get('electricitySpend')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('electricityConsumption')?.clearValidators();
        this.currentDetailsFormGroup.get('electricityConsumption')?.setValue('');
      } else {
        this.currentDetailsFormGroup.get('electricitySpend')?.clearValidators();
        this.currentDetailsFormGroup.get('electricitySpend')?.setValue('');
        this.currentDetailsFormGroup.get('electricityConsumption')?.clearValidators();
        this.currentDetailsFormGroup.get('electricityConsumption')?.setValue('');
      }

      // For Both and Gas
      if (fuelType === Resources.FuelType.BOTH || fuelType === Resources.FuelType.GAS) {
        this.currentDetailsFormGroup.get('gasSpend')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('gasConsumption')?.clearValidators();
        this.currentDetailsFormGroup.get('gasConsumption')?.setValue('');
      } else {
        this.currentDetailsFormGroup.get('gasSpend')?.clearValidators();
        this.currentDetailsFormGroup.get('gasSpend')?.setValue('');
        this.currentDetailsFormGroup.get('gasConsumption')?.clearValidators();
        this.currentDetailsFormGroup.get('gasConsumption')?.setValue('');
      }
    } else if (energyProfile === 'energyUsage') {
      // For Both and Electricity
      if (fuelType === Resources.FuelType.BOTH || fuelType === Resources.FuelType.ELECTRICITY) {
        // Current Spend selected - set validators for spend fields
        this.currentDetailsFormGroup.get('electricityConsumption')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('electricitySpend')?.clearValidators();
        this.currentDetailsFormGroup.get('electricitySpend')?.setValue('');
      } else {
        this.currentDetailsFormGroup.get('electricitySpend')?.clearValidators();
        this.currentDetailsFormGroup.get('electricitySpend')?.setValue('');
        this.currentDetailsFormGroup.get('electricityConsumption')?.clearValidators();
        this.currentDetailsFormGroup.get('electricityConsumption')?.setValue('');
      }

      // For Both and Gas
      if (fuelType === Resources.FuelType.BOTH || fuelType === Resources.FuelType.GAS) {
        this.currentDetailsFormGroup.get('gasConsumption')?.setValidators(Validators.required);
        this.currentDetailsFormGroup.get('gasSpend')?.clearValidators();
        this.currentDetailsFormGroup.get('gasSpend')?.setValue('');
      } else {
        this.currentDetailsFormGroup.get('gasSpend')?.clearValidators();
        this.currentDetailsFormGroup.get('gasSpend')?.setValue('');
        this.currentDetailsFormGroup.get('gasConsumption')?.clearValidators();
        this.currentDetailsFormGroup.get('gasConsumption')?.setValue('');
      }
    } else {
      // No selection - clear validators for both spend and consumption fields
      this.currentDetailsFormGroup.get('electricitySpend')?.clearValidators();
      this.currentDetailsFormGroup.get('electricitySpend')?.setValue('');
      this.currentDetailsFormGroup.get('electricityConsumption')?.clearValidators();
      this.currentDetailsFormGroup.get('electricityConsumption')?.setValue('');
      this.currentDetailsFormGroup.get('gasSpend')?.clearValidators();
      this.currentDetailsFormGroup.get('gasSpend')?.setValue('');
      this.currentDetailsFormGroup.get('gasConsumption')?.clearValidators();
      this.currentDetailsFormGroup.get('gasConsumption')?.setValue('');
    }

    // Update validity
    this.currentDetailsFormGroup.get('electricitySpend')?.updateValueAndValidity();
    this.currentDetailsFormGroup.get('electricityConsumption')?.updateValueAndValidity();
    this.currentDetailsFormGroup.get('gasSpend')?.updateValueAndValidity();
    this.currentDetailsFormGroup.get('gasConsumption')?.updateValueAndValidity();
  }

  // SignIn
  signIn(): void {
    const dialogRef = this.dialog.open(LoginComponent, {
      data: { placement: 'header_sign_in', target: 'login' },
      width: '570px',
      maxWidth: window.innerWidth <= 767 ? '95vw' : '569px',
      disableClose: true,
      autoFocus: false,
      panelClass: 'ctm-modal'
    });

    // After the dialog is closed, check the result
    dialogRef.afterClosed().subscribe(result => {
      // when sign in popup closed
    });
  }

  // Change postcode selection
  changeAddress(): void {
    this.selectedAddress = undefined;
    this.isReset = true;
  }

  // Get address details
  getAddressDetails(address: any): void {
    this.selectedAddress = address?.selectedAddress;

    // Get suppliers and tariffs
    this.getSuppliersAndTariff();

    // Get current usage
    this.getCurrentUsage();
  }

  // Back
  back(): void {
    if (this.queryParams?._gl) {
      window.open(`https://www.comparethemarket.com/energy/?_gl=${this.queryParams?._gl}`, '_self');
    } else {
      window.open(`https://www.comparethemarket.com/energy`, '_self');
    }
  }

  // Get current usage
  getCurrentUsage(): void {
    this.isExceptionalFlow = false;
    this.isCurrentUsageLoading = true;
    this.isConsumptionSpendMissing = false;

    // Edge case: Id addressId not available then no need to call a current usage API as it will fail, instead show the form to fill supplier and tariff manually. This is to avoid the scenario where user is not able to proceed after selecting an address due to API failure.
    if (!this.selectedAddress?.addressId) {
      // Navigate to postcode lookup page with selected address details again
      this.router.navigate(['/']);
      return;
    }

    const payload: any = {
      address_id: this.selectedAddress?.addressId,
      postcode: this.selectedAddress?.addressPostcode,
      aquistion_party: 'UI',
      gbgVersion: this.selectedAddress?.gbgVersion
    };

    if (this.isLoggedIn) {
      // Logged-In flow
      payload['email'] = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Email);
      payload['addressLine'] = this.selectedAddress?.addressText;

      if (this.selectedAddress?.mpan || this.savedDetails?.electricDetails?.mpan || this.savedDetails?.mpan) {
        payload['mpan'] = this.selectedAddress?.mpan || this.savedDetails?.electricDetails?.mpan || this.savedDetails?.mpan;
      }

      if (this.selectedAddress?.mprn || this.savedDetails?.gasDetails?.mprn || this.savedDetails?.mprn) {
        payload['mprn'] = this.selectedAddress?.mprn || this.savedDetails?.gasDetails?.mprn || this.savedDetails?.mprn;
      }
    }

    this.subscriptions.push(
      this.restAPI.postAPI('/v1/users/currentusageWrapper', payload).subscribe({
        next: (res: any) => {
          // Normalise correlation_id
          if (res?.correlation_id) {
            res.correlationId = res.correlation_id;
            delete res.correlation_id;
          }

          res['addressId'] = this.selectedAddress?.addressId;

          this.currentUsage = res;
          this.correlationId = this.currentUsage?.correlationId;

          this.fuelType = this.utilityService.getFuelType(this.currentUsage, 'currentUsage') || '';

          // Exception flow happen then no need to ask current details here will ask in the edit flow.
          // Edge case - In the current usage data, if details is missing, then open exceptional flow and return
          if (this.fuelType === Resources.EnergyType.BOTH && ((!this.currentUsage?.electricity?.supplier || !this.currentUsage?.gas?.supplier)
            || (this.currentUsage?.electricity?.tarrif ? !this.currentUsage?.electricity?.tarrif : !this.currentUsage?.electricity?.tariff)
            || (this.currentUsage?.gas?.tarrif ? !this.currentUsage?.gas?.tarrif : !this.currentUsage?.gas?.tariff)
            || (!this.currentUsage?.electricity?.paymentMethod || !this.currentUsage?.gas?.paymentMethod)
            || (!this.currentUsage?.electricity?.elecAnnualCostWithStandingCharge || !this.currentUsage?.gas?.gasAnnualCostWithStandingCharge)
            || (!this.currentUsage?.electricity?.consumption && !this.currentUsage?.gas?.consumption)
          )
          ) {
            // Fuel type both and missing details
            this.isExceptionalFlow = true;
            return;
          } else if (this.fuelType === Resources.EnergyType.ELECTRICITY && (!this.currentUsage?.electricity?.supplier
            || (this.currentUsage?.electricity?.tarrif ? !this.currentUsage?.electricity?.tarrif : !this.currentUsage?.electricity?.tariff)
            || !this.currentUsage?.electricity?.paymentMethod
            || !this.currentUsage?.electricity?.elecAnnualCostWithStandingCharge
            || !this.currentUsage?.electricity?.consumption
          )
          ) {
            // Fuel type electricity and missing details
            this.isExceptionalFlow = true;
            return;
          } else if (this.fuelType === Resources.EnergyType.GAS && (!this.currentUsage?.gas?.supplier
            || (this.currentUsage?.gas?.tarrif ? !this.currentUsage?.gas?.tarrif : !this.currentUsage?.gas?.tariff)
            || !this.currentUsage?.gas?.paymentMethod
            || !this.currentUsage?.gas?.gasAnnualCostWithStandingCharge
            || !this.currentUsage?.gas?.consumption
          )
          ) {
            // Fuel type gas and missing details
            this.isExceptionalFlow = true;
            return;
          }

          // Update MPAN and MPRN
          // MPAN
          if (this.currentUsage?.mpan) {
            this.selectedAddress!.mpan = this.currentUsage?.mpan;
          }
          // MPRN
          if (this.currentUsage?.mprn) {
            this.selectedAddress!.mprn = this.currentUsage?.mprn;
          }

          // Store into session storage
          this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CorrelationId, this.correlationId);
          this.sessionService.setData(Resources.SessionStorageKeys.DnoId, this.selectedAddress?.dnoId || this.currentUsage?.dnoRegion);
          this.sessionService.setData(Resources.SessionStorageKeys.SelectedAddress, this.selectedAddress);
          this.sessionService.setData(Resources.SessionStorageKeys.CurrentUsage, this.currentUsage);


          this.isCurrentUsageLoading = false;

          // Pre-populate supplier and tariff based on current usage and Supplier Tariff API response
          this.prePopulateSupplierAndTariff();
        }, error: (error) => {
          console.error('Error fetching current usage:', error);
          this.isCurrentUsageLoading = false;
          this.correlationId = error?.error?.msg?.correlationId;

          if (this.correlationId) {
            this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CorrelationId, this.correlationId);
          }

          this.isExceptionalFlow = true;
          // Store marked as exceptional flow in session storage to use it in edit details page as well
          this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsExceptionFlow, 'true');
        }
      })
    );
  }

  // Get suppliers and tariff
  getSuppliersAndTariff(): void {
    this.isSupplierLoading = true;
    const triggerAPITime = new Date().getTime();

    const payload = {
      dnoId: this.selectedAddress?.dnoId,
      priceDataRequired: true,
      uuidToken: uuidv4()
    };

    this.subscriptions.push(
      this.restAPI.postAPI('/v2/suppliertariffs', payload).subscribe({
        next: (res) => {
          // Store all suppliers
          this.fuelType = this.currentDetailsFormGroup?.get('fuelType')?.value;
          this.listOfSuppliers = res?.fuels;

          this.bothSupplierList = this.listOfSuppliers['electricitygas']?.suppliers || [];
          this.electricitySupplierList = this.listOfSuppliers['electricity']?.suppliers || [];
          this.gasSupplierList = this.listOfSuppliers['gas']?.suppliers || [];

          this.isSupplierLoading = false;

          if (!this.isExceptionalFlow) {
            // Pre-populate supplier and tariff based on current usage and Supplier Tariff API response
            this.prePopulateSupplierAndTariff();
          }
        }, error: (error) => {
          this.isSupplierLoading = false;
          const responseTime = new Date().getTime() - triggerAPITime;
          this.utilityService.sendAPIError('Supplier Tariffs API Failed', { supplierTariffs: payload }, { supplierTariffs: error }, responseTime);
        }
      })
    );
  }

  // Auto-populate supplier and tariff based on current usage and Supplier Tariff API response
  prePopulateSupplierAndTariff(): void {
    // Only pre-populate when both current usage and supplier loading is completed to avoid pre-population with incomplete data
    if (!this.isCurrentUsageLoading && !this.isSupplierLoading) {
      // FuelType selection
      this.fuelType = this.utilityService.getFuelType(this.currentUsage, 'current-usage') || '';
      this.currentDetailsFormGroup.get('fuelType')?.setValue(this.fuelType === Resources.EnergyType.BOTH ? Resources.FuelType.BOTH : this.fuelType === Resources.EnergyType.ELECTRICITY ? Resources.FuelType.ELECTRICITY : Resources.FuelType.GAS);

      // If FuelType is Both
      if (this.fuelType === Resources.FuelType.BOTH) {
        // Check if same supplier or not
        if (this.currentUsage?.electricity?.supplier && this.currentUsage?.gas?.supplier && this.currentUsage?.electricity?.supplier === this.currentUsage?.gas?.supplier) {
          this.currentDetailsFormGroup.get('isSameSupplier')?.setValue('Yes');
          const supplier = this.bothSupplierList.find((supplier: ISupplierDetails) => supplier.name === this.currentUsage?.electricity?.supplier || supplier.name === this.currentUsage?.gas?.supplier);
          if (supplier) {
            this.currentDetailsFormGroup.get('supplierName')?.setValue(supplier);
            this.currentDetailsFormGroup.get('supplierName')?.updateValueAndValidity();
          }

          // Check if same tariff or not
          const ElectricityTariff = this.currentUsage?.electricity?.tarrif ? this.currentUsage?.electricity?.tarrif : this.currentUsage?.electricity?.tariff;
          const GasTariff = this.currentUsage?.gas?.tarrif ? this.currentUsage?.gas?.tarrif : this.currentUsage?.gas?.tariff;

          if (ElectricityTariff && GasTariff && ElectricityTariff === GasTariff) {
            // Payment method
            this.currentDetailsFormGroup.get('paymentMethod')?.setValue(this.paymentList.find(payment => payment.id === Resources.reversePaymentOption[this.currentUsage?.electricity?.paymentMethod || this.currentUsage?.gas?.paymentMethod]));

            const tariff = this.bothTariffList.find((tariff: ISupplierTariff) => tariff.name === ElectricityTariff || tariff.name === GasTariff);
            if (tariff) {
              this.currentDetailsFormGroup.get('supplierTariff')?.setValue(tariff);
              this.currentDetailsFormGroup.get('supplierTariff')?.updateValueAndValidity();
            }
          } else {
            // Electricity Payment method
            this.currentDetailsFormGroup.get('electricityPaymentMethod')?.setValue(this.electricityPaymentList.find(payment => payment.id === Resources.reversePaymentOption[this.currentUsage?.electricity?.paymentMethod]));
            // Gas Payment method
            this.currentDetailsFormGroup.get('gasPaymentMethod')?.setValue(this.gasPaymentList.find(payment => payment.id === Resources.reversePaymentOption[this.currentUsage?.gas?.paymentMethod]));

            const elecTariff = this.electricityTariffList.find((tariff: ISupplierTariff) => tariff.name === ElectricityTariff);
            const gasTariff = this.gasTariffList.find((tariff: ISupplierTariff) => tariff.name === GasTariff);

            if (elecTariff) {
              this.currentDetailsFormGroup.get('electricitySupplierTariff')?.setValue(elecTariff);
              this.currentDetailsFormGroup.get('electricitySupplierTariff')?.updateValueAndValidity();
            }
            if (gasTariff) {
              this.currentDetailsFormGroup.get('gasSupplierTariff')?.setValue(gasTariff);
              this.currentDetailsFormGroup.get('gasSupplierTariff')?.updateValueAndValidity();
            }
          }
        } else {
          this.currentDetailsFormGroup.get('isSameSupplier')?.setValue('No');
          const elecSupplier = this.electricitySupplierList.find((supplier: ISupplierDetails) => supplier.name === this.currentUsage?.electricity?.supplier);
          const gasSupplier = this.gasSupplierList.find((supplier: ISupplierDetails) => supplier.name === this.currentUsage?.gas?.supplier);

          if (elecSupplier) {
            this.currentDetailsFormGroup.get('electricitySupplierName')?.setValue(elecSupplier);
            this.currentDetailsFormGroup.get('electricitySupplierName')?.updateValueAndValidity();
          }

          if (gasSupplier) {
            this.currentDetailsFormGroup.get('gasSupplierName')?.setValue(gasSupplier);
            this.currentDetailsFormGroup.get('gasSupplierName')?.updateValueAndValidity();
          }
        }

        // Checking both spend and consumption to decide energy profile selection and pre-populate respective fields based on that. If both spend and consumption is available then prefer spend data for pre-population and energy profile selection.
        const bothSpend = Number(this.currentUsage?.electricity?.elecAnnualCostWithStandingCharge || 0) + Number(this.currentUsage?.gas?.gasAnnualCostWithStandingCharge || 0);
        const bothConsumption = Number(this.currentUsage?.electricity?.consumption || 0) + Number(this.currentUsage?.gas?.consumption || 0);

        // Energy profile and duration
        this.currentDetailsFormGroup.get('energyProfile')?.setValue(!!bothSpend ? 'currentSpend' : !!bothConsumption ? 'energyUsage' : '');
        this.currentDetailsFormGroup.get('duration')?.setValue('annual');

        // In case, if spend and consumption data any one of them is missing in current usage API response then ask from the user to fill manually instead of pre-populating with incomplete data.
        if ((!this.currentUsage?.electricity?.elecAnnualCostWithStandingCharge || !this.currentUsage?.gas?.gasAnnualCostWithStandingCharge) || (!this.currentUsage?.electricity?.consumption || !this.currentUsage?.gas?.consumption)) {
          this.isConsumptionSpendMissing = true;
          return;
        }

        // Pre-populate spend or consumption based on energy profile selection
        // if (this.currentDetailsFormGroup.get('energyProfile')?.value === 'currentSpend') {
        //   this.currentDetailsFormGroup.get('electricitySpend')?.setValue(this.currentUsage?.electricity?.elecAnnualCostWithStandingCharge);
        //   this.currentDetailsFormGroup.get('gasSpend')?.setValue(this.currentUsage?.gas?.gasAnnualCostWithStandingCharge);
        // } else {
        //   this.currentDetailsFormGroup.get('electricityConsumption')?.setValue(this.currentUsage?.electricity?.consumption);
        //   this.currentDetailsFormGroup.get('gasConsumption')?.setValue(this.currentUsage?.gas?.consumption);
        // }
      } else if (this.fuelType === Resources.FuelType.ELECTRICITY) {
        const elecSupplier = this.electricitySupplierList.find((supplier: ISupplierDetails) => supplier.name === this.currentUsage?.electricity?.supplier);

        if (elecSupplier) {
          this.currentDetailsFormGroup.get('electricitySupplierName')?.setValue(elecSupplier);
          this.currentDetailsFormGroup.get('electricitySupplierName')?.updateValueAndValidity();
        }

        // Electricity Payment method
        this.currentDetailsFormGroup.get('electricityPaymentMethod')?.setValue(this.electricityPaymentList.find(payment => payment.id === Resources.reversePaymentOption[this.currentUsage?.electricity?.paymentMethod]));

        // Electricity Tariff
        const ElectricityTariff = this.currentUsage?.electricity?.tarrif ? this.currentUsage?.electricity?.tarrif : this.currentUsage?.electricity?.tariff;
        const elecTariff = this.electricityTariffList.find((tariff: ISupplierTariff) => tariff.name === ElectricityTariff);
        if (elecTariff) {
          this.currentDetailsFormGroup.get('electricitySupplierTariff')?.setValue(elecTariff);
          this.currentDetailsFormGroup.get('electricitySupplierTariff')?.updateValueAndValidity();
        }

        // Checking both spend and consumption to decide energy profile selection and pre-populate respective fields based on that. If both spend and consumption is available then prefer spend data for pre-population and energy profile selection.
        const bothSpend = Number(this.currentUsage?.electricity?.elecAnnualCostWithStandingCharge || 0);
        const bothConsumption = Number(this.currentUsage?.electricity?.consumption || 0);

        // Energy profile and duration
        this.currentDetailsFormGroup.get('energyProfile')?.setValue(!!bothSpend ? 'currentSpend' : !!bothConsumption ? 'energyUsage' : '');
        this.currentDetailsFormGroup.get('duration')?.setValue('annual');

        // In case, if spend and consumption data any one of them is missing in current usage API response then ask from the user to fill manually instead of pre-populating with incomplete data.
        if ((!this.currentUsage?.electricity?.elecAnnualCostWithStandingCharge) || (!this.currentUsage?.electricity?.consumption)) {
          this.isConsumptionSpendMissing = true;
          return;
        }

        // // Pre-populate spend or consumption based on energy profile selection
        // if (this.currentDetailsFormGroup.get('energyProfile')?.value === 'currentSpend') {
        //   this.currentDetailsFormGroup.get('electricitySpend')?.setValue(this.currentUsage?.electricity?.elecAnnualCostWithStandingCharge);
        // } else {
        //   this.currentDetailsFormGroup.get('electricityConsumption')?.setValue(this.currentUsage?.electricity?.consumption);
        // }
      } else if (this.fuelType === Resources.FuelType.GAS) {
        const gasSupplier = this.gasSupplierList.find((supplier: ISupplierDetails) => supplier.name === this.currentUsage?.gas?.supplier);

        if (gasSupplier) {
          this.currentDetailsFormGroup.get('gasSupplierName')?.setValue(gasSupplier);
          this.currentDetailsFormGroup.get('gasSupplierName')?.updateValueAndValidity();
        }

        // Gas Payment method
        this.currentDetailsFormGroup.get('gasPaymentMethod')?.setValue(this.gasPaymentList.find(payment => payment.id === Resources.reversePaymentOption[this.currentUsage?.gas?.paymentMethod]));

        // Gas Tariff
        const GasTariff = this.currentUsage?.gas?.tarrif ? this.currentUsage?.gas?.tarrif : this.currentUsage?.gas?.tariff;
        const gasTariff = this.gasTariffList.find((tariff: ISupplierTariff) => tariff.name === GasTariff);
        if (gasTariff) {
          this.currentDetailsFormGroup.get('gasSupplierTariff')?.setValue(gasTariff);
          this.currentDetailsFormGroup.get('gasSupplierTariff')?.updateValueAndValidity();
        }

        // Checking both spend and consumption to decide energy profile selection and pre-populate respective fields based on that. If both spend and consumption is available then prefer spend data for pre-population and energy profile selection.
        const bothSpend = Number(this.currentUsage?.gas?.gasAnnualCostWithStandingCharge || 0);
        const bothConsumption = Number(this.currentUsage?.gas?.consumption || 0);

        // Energy profile and duration
        this.currentDetailsFormGroup.get('energyProfile')?.setValue(!!bothSpend ? 'currentSpend' : !!bothConsumption ? 'energyUsage' : '');
        this.currentDetailsFormGroup.get('duration')?.setValue('annual');

        // In case, if spend and consumption data any one of them is missing in current usage API response then ask from the user to fill manually instead of pre-populating with incomplete data.
        if ((!this.currentUsage?.gas?.gasAnnualCostWithStandingCharge) || (!this.currentUsage?.gas?.consumption)) {
          this.isConsumptionSpendMissing = true;
          return;
        }

        // // Pre-populate spend or consumption based on energy profile selection
        // if (this.currentDetailsFormGroup.get('energyProfile')?.value === 'currentSpend') {
        //   this.currentDetailsFormGroup.get('gasSpend')?.setValue(this.currentUsage?.gas?.gasAnnualCostWithStandingCharge);
        // } else {
        //   this.currentDetailsFormGroup.get('gasConsumption')?.setValue(this.currentUsage?.gas?.consumption);
        // }
      }
    }
  }

  // Get payment method list based on supplier and fuel type
  getPaymentList(fuelType: string, supplier: any): void {
    if (fuelType === Resources.FuelType.BOTH) {
      this.paymentList = [];
      this.currentDetailsFormGroup.get('paymentMethod')?.setValue('');
      this.currentDetailsFormGroup.get('supplierTariff')?.setValidators(Validators.required);
      this.currentDetailsFormGroup.get('supplierTariff')?.updateValueAndValidity();

      this.currentDetailsFormGroup.get('electricitySupplierTariff')?.clearValidators();
      this.currentDetailsFormGroup.get('electricitySupplierTariff')?.setValue('');
      this.currentDetailsFormGroup.get('gasSupplierTariff')?.clearValidators();
      this.currentDetailsFormGroup.get('gasSupplierTariff')?.setValue('');

      this.currentDetailsFormGroup.get('electricitySupplierTariff')?.updateValueAndValidity();
      this.currentDetailsFormGroup.get('gasSupplierTariff')?.updateValueAndValidity();

      // If electricity prepayment is true then manually add the payment method as prepayment
      if (!this.paymentList.some((payment: any) => payment.id === 5) && (this.currentUsage?.electricity?.elePrePayment)) {
        this.paymentList.push({
          id: 5,
          name: (Resources.paymentOption as any)[5]
        });
      }

      // If gas prepayment is true then manually add the payment method as prepayment
      if (!this.paymentList.some((payment: any) => payment.id === 5) && (this.currentUsage?.gas?.gasPrePayment)) {
        this.paymentList.push({
          id: 5,
          name: (Resources.paymentOption as any)[5]
        });
      }

      // Iterate through the supplier tariffs and get the payment methods based on meter type and payment method
      supplier?.supplierTariffs?.map((tariff: any) => tariff.paymentMethods).flat().forEach((paymentMethod: any) => {
        // Check if payment method is already added to the list and if not, add it to the list and Add a check to not add the payment type 4 and 9
        if (!this.paymentList.some((payment: any) => payment.id === paymentMethod.id) && paymentMethod.id !== 4 && paymentMethod.id !== 9) {
          // If address is prepayment address then only add prepayment if available tariff then add it otherwise remove it
          if ((!this.currentUsage?.electricity?.elePrePayment && !this.currentUsage?.gas?.gasPrePayment) || paymentMethod.id === 5) {
            // Add payment method to the list only if it exists in Resources.paymentOption
            const paymentName = (Resources.paymentOption as any)[paymentMethod.id];
            if (paymentName) {
              this.paymentList.push({
                id: paymentMethod.id,
                name: paymentName
              });
            }
          }
        }
      });
    } else if (fuelType === Resources.FuelType.ELECTRICITY) {
      this.electricityPaymentList = [];
      this.currentDetailsFormGroup.get('electricityPaymentMethod')?.setValue('');

      this.currentDetailsFormGroup.get('electricitySupplierTariff')?.setValidators(Validators.required);
      this.currentDetailsFormGroup.get('electricitySupplierTariff')?.updateValueAndValidity();

      this.currentDetailsFormGroup.get('supplierTariff')?.clearValidators();
      this.currentDetailsFormGroup.get('supplierTariff')?.setValue('');
      this.currentDetailsFormGroup.get('supplierTariff')?.updateValueAndValidity();

      this.currentDetailsFormGroup.get('paymentMethod')?.clearValidators();
      this.currentDetailsFormGroup.get('paymentMethod')?.setValue('');
      this.currentDetailsFormGroup.get('paymentMethod')?.updateValueAndValidity();

      // If electricity prepayment is true then manually add the payment method as prepayment
      if (this.currentUsage?.electricity?.elePrePayment) {
        this.electricityPaymentList.push({
          id: 5,
          name: (Resources.paymentOption as any)[5]
        });
      }

      // Iterate through the supplier tariffs and get the payment methods based on meter type and payment method
      supplier?.supplierTariffs?.map((tariff: any) => tariff.paymentMethods).flat().forEach((paymentMethod: any) => {
        // Check if payment method is already added to the list and if not, add it to the list and Add a check to not add the payment type 4 and 9
        if (!this.electricityPaymentList.some((payment: any) => payment.id === paymentMethod.id) && paymentMethod.id !== 4 && paymentMethod.id !== 9) {
          // If address is prepayment address then only add prepayment if available tariff then add it otherwise remove it
          if (!this.currentUsage?.electricity?.elePrePayment || paymentMethod.id === 5) {
            // Add payment method to the list only if it exists in Resources.paymentOption
            const paymentName = (Resources.paymentOption as any)[paymentMethod.id];
            if (paymentName) {
              this.electricityPaymentList.push({
                id: paymentMethod.id,
                name: paymentName
              });
            }
          }
        }
      });
    } else if (fuelType === Resources.FuelType.GAS) {
      this.gasPaymentList = [];
      this.currentDetailsFormGroup.get('gasPaymentMethod')?.setValue('');

      this.currentDetailsFormGroup.get('gasSupplierTariff')?.setValidators(Validators.required);
      this.currentDetailsFormGroup.get('gasSupplierTariff')?.updateValueAndValidity();

      this.currentDetailsFormGroup.get('supplierTariff')?.clearValidators();
      this.currentDetailsFormGroup.get('supplierTariff')?.setValue('');
      this.currentDetailsFormGroup.get('supplierTariff')?.updateValueAndValidity();

      this.currentDetailsFormGroup.get('paymentMethod')?.clearValidators();
      this.currentDetailsFormGroup.get('paymentMethod')?.setValue('');
      this.currentDetailsFormGroup.get('paymentMethod')?.updateValueAndValidity();

      // If gas prepayment is true then manually add the payment method as prepayment
      if (this.currentUsage?.gas?.gasPrePayment) {
        this.gasPaymentList.push({
          id: 5,
          name: (Resources.paymentOption as any)[5]
        });
      }

      // Iterate through the supplier tariffs and get the payment methods based on meter type and payment method
      supplier?.supplierTariffs?.map((tariff: any) => tariff.paymentMethods).flat().forEach((paymentMethod: any) => {
        // Check if payment method is already added to the list and if not, add it to the list and Add a check to not add the payment type 4 and 9
        if (!this.gasPaymentList.some((payment: any) => payment.id === paymentMethod.id) && paymentMethod.id !== 4 && paymentMethod.id !== 9) {
          // If address is prepayment address then only add prepayment if available tariff then add it otherwise remove it
          if (!this.currentUsage?.gas?.gasPrePayment || paymentMethod.id === 5) {
            // Add payment method to the list only if it exists in Resources.paymentOption
            const paymentName = (Resources.paymentOption as any)[paymentMethod.id];
            if (paymentName) {
              this.gasPaymentList.push({
                id: paymentMethod.id,
                name: paymentName
              });
            }
          }
        }
      });
    }

    // Mannually add an option for "I'm not sure" but when selected then supplier filter by MDD only.
    this.paymentList.push({
      id: 1,
      name: "I'm not sure"
    });
  }

  // Get tariff list based on fuel type, payment method and supplier
  getTariffList(fuelType: string, paymentMethod: any, supplier: any): void {
    if (fuelType === 'Both') {
      this.bothTariffList = [];
      const meterType = this.getMeterType(this.currentUsage?.electricity?.metertype || this.currentUsage?.gas?.metertype || 'Traditional');

      this.bothTariffList = supplier?.supplierTariffs?.filter((tariff: any) => tariff.paymentMethods.some((payment: any) => payment.id === paymentMethod.id) && tariff.meterTypes.includes(meterType)) || [];

      // Electricity Consumption
      this.currentDetailsFormGroup.get('electricityConsumption')?.setValidators(Validators.required);
      this.currentDetailsFormGroup.get('electricityConsumption')?.updateValueAndValidity();
      // set electricity consumption if available in current usage
      if (this.currentUsage?.electricity?.consumption) {
        this.currentDetailsFormGroup.get('electricityConsumption')?.setValue(this.currentUsage?.electricity?.consumption);
      }

      // Gas Consumption
      this.currentDetailsFormGroup.get('gasConsumption')?.setValidators(Validators.required);
      this.currentDetailsFormGroup.get('gasConsumption')?.updateValueAndValidity();
      // set gas consumption if available in current usage
      if (this.currentUsage?.gas?.consumption) {
        this.currentDetailsFormGroup.get('gasConsumption')?.setValue(this.currentUsage?.gas?.consumption);
      }
    } else if (fuelType === 'Electricity') {
      this.electricityTariffList = [];
      const meterType = this.getMeterType(this.currentUsage?.electricity?.metertype || 'Traditional');

      this.electricityTariffList = supplier?.supplierTariffs?.filter((tariff: any) => tariff.paymentMethods.some((payment: any) => payment.id === paymentMethod.id) && tariff.meterTypes.includes(meterType)) || [];

      // Electricity Consumption
      this.currentDetailsFormGroup.get('electricityConsumption')?.setValidators(Validators.required);
      this.currentDetailsFormGroup.get('electricityConsumption')?.updateValueAndValidity();
      // set electricity consumption if available in current usage
      if (this.currentUsage?.electricity?.consumption) {
        this.currentDetailsFormGroup.get('electricityConsumption')?.setValue(this.currentUsage?.electricity?.consumption);
      }
    } else if (fuelType === 'Gas') {
      this.gasTariffList = [];
      const meterType = this.getMeterType(this.currentUsage?.gas?.metertype || 'Traditional');

      this.gasTariffList = supplier?.supplierTariffs?.filter((tariff: any) => tariff.paymentMethods.some((payment: any) => payment.id === paymentMethod.id) && tariff.meterTypes.includes(meterType)) || [];

      // Gas Consumption
      this.currentDetailsFormGroup.get('gasConsumption')?.setValidators(Validators.required);
      this.currentDetailsFormGroup.get('gasConsumption')?.updateValueAndValidity();
      // set gas consumption if available in current usage
      if (this.currentUsage?.gas?.consumption) {
        this.currentDetailsFormGroup.get('gasConsumption')?.setValue(this.currentUsage?.gas?.consumption);
      }
    }
  }

  // Get meter type
  getMeterType(meterType: string): string {
    const meterTypeSplit = meterType.split('_');
    if (meterTypeSplit.length > 1) {
      return meterTypeSplit[0] === 'Traditional' ? 'STD' : meterTypeSplit[0] === 'Economy7' ? 'E7' : meterTypeSplit[0] === 'Prepayment' ? 'PP' : meterTypeSplit[0];
    } else {
      return meterType === 'Traditional' ? 'STD' : meterType === 'Economy7' ? 'E7' : meterType === 'Prepayment' ? 'PP' : meterType;
    }
  }

  // Selected Marketing consent
  selectedMarketingConsent(event: any, options: any): void {
    if (event.source.value === "Don't contact me") {
      if (event.checked) {
        this.selectedConsent.push(event.source.value);
        this.selectedConsent = this.selectedConsent.filter((consent) => consent === "Don't contact me");
      } else {
        this.selectedConsent = this.selectedConsent.filter((consent) => consent !== "Don't contact me");
      }
      this.marketingConsentList.map((option: any) => {
        option['isChecked'] = (option.name === "Don't contact me");
      })
    } else {
      this.marketingConsentList.map((option: any) => {
        if (option.name === event.source.value) {
          option['isChecked'] = event.checked;
        }

        if (option.name === "Don't contact me") {
          option['isChecked'] = false;
        }
      });
      if (event.checked) {
        this.selectedConsent.push(event.source.value);
        this.selectedConsent = this.selectedConsent.filter((consent) => consent !== "Don't contact me");
      } else {
        this.selectedConsent = this.selectedConsent.filter((consent) => consent !== event.source.value);
      }
    }

    // Update form control
    this.contactDetailsFormGroup.patchValue({
      consent: (this.selectedConsent).join(', ')
    });

    // Tealium CTM marketing contact preferences
    const contactPreferences = [];
    // Iterate and create contact preferences
    this.selectedConsent.map((preference: string) => {
      const preferenceData = {};
      preferenceData[preference] = true;
      contactPreferences.push(preferenceData);
    });

    this.contactDetailsFormGroup.get('consent')?.markAllAsTouched();
  }

  // is fuelType based possible scenarios covered check
  isFormCompleted(): boolean {
    // FuelType Both
    if (this.fuelType === Resources.FuelType.BOTH) {
      // Is same supplier or not
      if (this.currentDetailsFormGroup.get('isSameSupplier')?.value === 'Yes') {
        // If same tariff is yes then supplier tariff is required
        return !!this.currentDetailsFormGroup.get('supplierTariff')?.value || false;
      } else {
        // not same supplier then both supplier name is required
        return !!this.currentDetailsFormGroup.get('electricitySupplierTariff')?.value && !!this.currentDetailsFormGroup.get('gasSupplierTariff')?.value || false;
      }
    } else if (this.fuelType === Resources.FuelType.ELECTRICITY) {
      return !!this.currentDetailsFormGroup.get('electricitySupplierTariff')?.value || false;
    } else if (this.fuelType === Resources.FuelType.GAS) {
      return !!this.currentDetailsFormGroup.get('gasSupplierTariff')?.value || false;
    }
    return false;
  }

  // Submit
  submit(): void {
    // Prevent submission if already submitting, checking currentDetailFormGroup is valid and checking contactDetailsFormGroup is valid to make sure marketing consent is selected as well before submission.
    if (!this.isExceptionalFlow ? !this.currentDetailsFormGroup?.valid || !this.contactDetailsFormGroup?.valid : !this.contactDetailsFormGroup) {
      this.currentDetailsFormGroup?.markAllAsTouched();
      this.contactDetailsFormGroup?.markAllAsTouched();
      return;
    } else {
      this.isSubmitting = true;

       // Tealium link event
      this.tealiumService.link({
        tealium_event: 'quote_submit'
      });

      // end_quote is emitted once the customer arrives on the results page
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsTealiumEndQuotePending, true);

      if (!this.correlationId) {
        this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsNewFlow, true);
        this.navigateToDashboard();
        return;
      }

      // Edge case: If gas is non domestic then show warning message about switch will failed from the supply due to this address is non domestic address.
      if (this.currentUsage?.isGasDomestic === false) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          data: {
            pageName: 'NonDomesticGasWarning',
            title: 'Is your gas supply for your home?',
            message: 'We\'ve detected your gas supply may be registered as a business.If it\'s for your home, continuing could cause your switch to fail. Check with your supplier if you\'re unsure.',
            confirmText: 'It\'s for my home',
            cancelText: 'It\'s for a business'
          },
          maxWidth: window.innerWidth <= 767 ? '95vw' : '600px',
          disableClose: false,
          autoFocus: false,
          panelClass: 'ctm-modal'
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            // User chose "No, it's Domestic" — proceed with the domestic flow
            this.proceedWithSubmit();
          } else {
            window.open(`https://business-energy.comparethemarket.com/service-dom?postcode=${this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Postcode)}`, '_blank');
            this.isSubmitting = false;
          }
        });
        return;
      }

      // If gas is domestic or no gas, proceed normally
      this.proceedWithSubmit();
    }
  }

  // Proceed with submission after validation
  private proceedWithSubmit(): void {
   // Added a flag to enable normal flow bypass to check authentication when user is not loggedIN flow.
    if (!this.utilityService.isLoggedIn()) {
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsRetry, true);
    } else {
      this.sessionService.removeItemFromSession(Resources.SessionStorageKeys.IsRetry);
    }

    if (!this.correlationId) {
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsNewFlow, true);
      this.navigateToDashboard();
      return;
    } else if (this.currentUsage) {
      // User creation
      this.userCreation();
    } else {
      // Get current usage
      this.getCurrentUsage();
    }
  }

  // Calculated spend usage
  calculatedSpendUsage(): void {
    const currentDetails: any = this.currentDetailsFormGroup?.value;

    const payload = {
      options: {
        spend: false,
        usage: true
      },
      data: {
        fuelType: currentDetails?.fuelType === 'Both' ? Resources.EnergyType.BOTH : (currentDetails?.fuelType === 'Electricity') ? Resources.EnergyType.ELECTRICITY : Resources.EnergyType.GAS,
      }
    };

    // FuelType Both
    if (payload?.data?.fuelType === Resources.EnergyType.BOTH) {
      payload.data['meterTypeElec'] = this.getMeterType(this.currentUsage?.electricity?.metertype || 'Traditional');
      payload.data['UnitRate1Elec'] = currentDetails?.isSameSupplier === 'Yes'
        ? currentDetails?.supplierTariff?.UnitRate1Elec
        : currentDetails?.electricitySupplierTariff?.UnitRate1Elec;
      payload.data['StandingChargeElec'] = currentDetails?.isSameSupplier === 'Yes'
        ? currentDetails?.supplierTariff?.StandingChargeElec
        : currentDetails?.electricitySupplierTariff?.StandingChargeElec;
      payload.data['elecUsage'] = currentDetails?.electricityConsumption;

      // If meterType is E7, then add UnitRateE7Elec
      if (payload.data['meterTypeElec'] === 'E7') {
        payload.data['UnitRateE7Elec'] = currentDetails?.isSameSupplier === 'Yes'
          ? currentDetails?.supplierTariff?.UnitRateE7Elec
          : currentDetails?.electricitySupplierTariff?.UnitRateE7Elec;
      }

      payload.data['meterTypeGas'] = this.getMeterType(this.currentUsage?.gas?.metertype || 'Traditional');
      payload.data['UnitRate1Gas'] = currentDetails?.isSameSupplier === 'Yes'
        ? currentDetails?.supplierTariff?.UnitRate1Gas
        : currentDetails?.gasSupplierTariff?.UnitRate1Gas;
      payload.data['StandingChargeGas'] = currentDetails?.isSameSupplier === 'Yes'
        ? currentDetails?.supplierTariff?.StandingChargeGas
        : currentDetails?.gasSupplierTariff?.StandingChargeGas;
      payload.data['gasUsage'] = currentDetails?.gasConsumption;
    } else if (payload?.data?.fuelType === Resources.EnergyType.ELECTRICITY) {
      payload.data['meterTypeElec'] = this.getMeterType(this.currentUsage?.electricity?.metertype || 'Traditional');
      payload.data['UnitRate1Elec'] = currentDetails?.electricitySupplierTariff?.UnitRate1Elec;
      payload.data['StandingChargeElec'] = currentDetails?.electricitySupplierTariff?.StandingChargeElec;
      payload.data['elecUsage'] = currentDetails?.electricityConsumption;

      // If meterType is E7, then add UnitRateE7Elec
      if (payload.data['meterTypeElec'] === 'E7') {
        payload.data['UnitRateE7Elec'] = currentDetails?.electricitySupplierTariff?.UnitRateE7Elec;
      }
    } else if (payload?.data?.fuelType === Resources.EnergyType.GAS) {
      payload.data['meterTypeGas'] = this.getMeterType(this.currentUsage?.gas?.metertype || 'Traditional');
      payload.data['UnitRate1Gas'] = currentDetails?.gasSupplierTariff?.UnitRate1Gas;
      payload.data['StandingChargeGas'] = currentDetails?.gasSupplierTariff?.StandingChargeGas;
      payload.data['gasUsage'] = currentDetails?.gasConsumption;
    }

    this.subscriptions.push(
      this.restAPI.postNewAPI('calculateSpendUsage', payload).subscribe({
        next: (res) => {
          this.calculatedSpend = res?.data;

          // Update current details with calculated spend usage response
          this.updateCurrentDetails();
        }, error: (error) => {
          this.isSubmitting = false;
          console.error('Error: ', error);
        }
      })
    );
  }

  // Put current usage
  updateCurrentDetails(): void {
    const currentDetails: any = this.currentDetailsFormGroup?.value;

    const payload = {
      fuelType: currentDetails?.fuelType === 'Both' ? Resources.EnergyType.BOTH : (currentDetails?.fuelType === 'Electricity') ? Resources.EnergyType.ELECTRICITY : Resources.EnergyType.GAS,
      dnoRegion: this.currentUsage?.dnoRegion || this.selectedAddress?.dnoId,
      addressBlockFlow: 'N',
      address_id: this.selectedAddress?.addressId,
      duration: 'Annually',
      source: 'current_usage',
      gbgVersion: this.selectedAddress?.gbgVersion,
      postcode: this.selectedAddress?.addressPostcode,
    }

    // Fuel Type Both or Electricity
    if (payload?.fuelType === Resources.EnergyType.BOTH || payload?.fuelType === Resources.EnergyType.ELECTRICITY) {
      const electricitySupplier = (payload?.fuelType === Resources.EnergyType.BOTH
        && currentDetails?.isSameSupplier === 'Yes')
        ? currentDetails?.supplierName
        : currentDetails?.electricitySupplierName;
      const electricitySupplierTariff = (payload?.fuelType === Resources.EnergyType.BOTH
        && currentDetails?.isSameSupplier === 'Yes')
        ? currentDetails?.supplierTariff
        : currentDetails?.electricitySupplierTariff;

      payload['mpan'] = this.currentUsage?.mpan;
      payload['electricity'] = {
        metertype: this.currentUsage?.electricity?.metertype,
        isSmart: this.currentUsage?.electricity?.isSmart,
        paymentMethod: currentDetails?.paymentMethod?.name || this.currentUsage?.electricity?.paymentMethod,
        elePrePayment: currentDetails?.paymentMethod?.name === 'Prepayment' ? true : this.currentUsage?.electricity?.elePrePayment,
        supplierId: electricitySupplier?.id || this.currentUsage?.electricity?.suppierId,
        supplier: electricitySupplier?.name || this.currentUsage?.electricity?.supplier,
        tariffId: electricitySupplierTariff?.id || this.currentUsage?.electricity?.tariffId,
        tariff: electricitySupplierTariff?.name || this.currentUsage?.electricity?.tariff,
        mpan: this.currentUsage?.mpan,
        elecAnnualCostWithStandingCharge: this.calculatedSpend?.elecAnnualCostWithStandingCharge,
        elecAnnualCostWithoutStandingCharge: this.calculatedSpend?.elecAnnualCostWithoutStandingCharge,
        usage: currentDetails?.electricityConsumption,
        usagePeriod: '3'
      };
    }

    // Fuel Type Both or Gas
    if (payload?.fuelType === Resources.EnergyType.BOTH || payload?.fuelType === Resources.EnergyType.GAS) {
      const gasSupplier = (payload?.fuelType === Resources.EnergyType.BOTH
        && currentDetails?.isSameSupplier === 'Yes')
        ? currentDetails?.supplierName
        : currentDetails?.gasSupplierName;
      const gasSupplierTariff = (payload?.fuelType === Resources.EnergyType.BOTH
        && currentDetails?.isSameSupplier === 'Yes')
        ? currentDetails?.supplierTariff
        : currentDetails?.gasSupplierTariff;

      payload['mprn'] = this.currentUsage?.mprn;
      payload['gas'] = {
        metertype: this.currentUsage?.gas?.metertype,
        isSmart: this.currentUsage?.gas?.isSmart,
        paymentMethod: currentDetails?.paymentMethod?.name || this.currentUsage?.gas?.paymentMethod,
        gasPrePayment: currentDetails?.paymentMethod?.name === 'Prepayment' ? true : this.currentUsage?.gas?.gasPrePayment,
        supplierId: gasSupplier?.id || this.currentUsage?.gas?.suppierId,
        supplier: gasSupplier?.name || this.currentUsage?.gas?.supplier,
        tariffId: gasSupplierTariff?.id || this.currentUsage?.gas?.tariffId,
        tariff: gasSupplierTariff?.name || this.currentUsage?.gas?.tariff,
        mprn: this.currentUsage?.mprn,
        gasAnnualCostWithStandingCharge: this.calculatedSpend?.gasAnnualCostWithStandingCharge,
        gasAnnualCostWithoutStandingCharge: this.calculatedSpend?.gasAnnualCostWithoutStandingCharge,
        usage: currentDetails?.gasConsumption,
        usagePeriod: '3'
      };
    }

    this.restAPI.putAPI(`/v1/users/currentusageWrapper?correlationId=${this.correlationId}`, payload).subscribe({
      next: (data: any) => {
        if (data?.correlation_id) {
          data['correlationId'] = data?.correlation_id;
          delete data['correlation_id'];
        }
        payload['correlationId'] = data?.correlationId;

        // Store into session storage
        this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CorrelationId, data?.correlationId);
        this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CurrentUsage, payload);

        // User creation
        this.userCreation();
      }, error: (error) => {
        this.isSubmitting = false;
        console.error('Error updating current details: ', error);
      }
    });
  }

  // User creation
  userCreation(): void {
    const markertingConsent = this.contactDetailsFormGroup?.get('consent')?.value || this.CtmMarketingConsent;

    const triggerTime = new Date().getTime();
    const payload = {
      user: {
        email: (this.contactDetailsFormGroup?.get('email')?.value)?.trim()?.toLowerCase(),
        uuidToken: uuidv4(),
        switchRecommendation: (markertingConsent !== 'Don\'t contact me'),
        contactConsent: markertingConsent !== 'Don\'t contact me' ? markertingConsent?.split(',') : [],
        marketingConsent: (markertingConsent !== 'Don\'t contact me'),
      }
    };

    // Personal details
    if (this.personalDetails) {
      if (this.personalDetails?.firstName) {
        payload.user['firstName'] = this.personalDetails?.firstName;
      }
      if (this.personalDetails?.lastName) {
        payload.user['lastName'] = this.personalDetails?.lastName;
      }
      if (this.personalDetails?.phone) {
        payload.user['mobile'] = this.personalDetails?.phone;
      }
    }

    this.subscriptions.push(
      this.restAPI.postAPI(`/v1/users?correlationId=${this.correlationId}`, payload).subscribe({
        next: (res) => {
          // User onboarding
          this.userOnBoarding();
        }, error: (err) => {
          console.log('Error fetching user creation:', err);
          // If email is not valid then show error message
          if (err?.error?.additionalAttributes?.attribute === 'email') {
            // If email is not valid then show error message
            this.contactDetailsFormGroup.get('email')?.setErrors({ 'invalid': err?.error?.additionalAttributes?.displayText });

            setTimeout(() => {
              // Scroll to the email field
              const errorElementRef = document.querySelector('.ng-invalid');
              if (errorElementRef) {
                errorElementRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          } else if (err?.error?.error === 'Invalid request payload') {
            // If payload is invalid then show error message
            this.openPopup('We’re unable to process your request at the moment. Please try again later.');
          }
          const responseTime = (new Date().getTime() - triggerTime) / 1000;
          this.isSubmitting = false;

          // Trigger error email
          this.utilityService.sendAPIError('User creation API Failed', { userCreation: payload }, { userCreation: err }, responseTime);
        }
      })
    );
  }

  // User onboarding
  userOnBoarding(): void {
    const triggerTime = new Date().getTime();
    const payload = {
      email: (this.contactDetailsFormGroup?.get('email')?.value)?.trim()?.toLowerCase(),
      correlationId: this.correlationId,
      uuidToken: uuidv4()
    };

    this.subscriptions.push(
      this.restAPI.postAPI('/v1/users/onboard', payload).subscribe({
        next: (data) => {
          const fuelType = this.utilityService.getFuelType(this.currentUsage, 'current-usage');
          if ((fuelType === Resources.EnergyType.BOTH && (!this.currentUsage?.electricity?.gocoSupplier || !this.currentUsage?.electricity?.tarrif || !this.currentUsage?.gas?.gocoSupplier || !this.currentUsage?.gas?.tarrif))
            || (fuelType === Resources.EnergyType.ELECTRICITY && (!this.currentUsage?.electricity?.gocoSupplier || !this.currentUsage?.electricity?.tarrif))
            || (fuelType === Resources.EnergyType.GAS && (!this.currentUsage?.gas?.gocoSupplier || !this.currentUsage?.gas?.tarrif))
            || !this.currentUsage?.dnoRegion
          ) {
            this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsNewFlow, true);
            // Navigate to dashboard
            this.navigateToDashboard();
          } else {
            // Navigate to quote
            this.navigateToQuote();
          }
        }, error: (err) => {
          console.log('Error fetching user onboarding:', err);
          const responseTime = (new Date().getTime() - triggerTime) / 1000;
          this.isSubmitting = false;

          // Trigger error email
          this.utilityService.sendAPIError('User onboarding API Failed', { userOnboarding: payload }, { userOnboarding: err }, responseTime);
        }
      })
    );
  }

  // Navigate to dashboard
  navigateToDashboard(): void {
    // Set the email in local storage
    const userEmail = this.contactDetailsFormGroup?.get('email')?.value?.trim()?.toLowerCase();
    this.postcode = this.selectedAddress?.addressPostcode || this.selectedAddress?.postcode || '';
    this.sessionService.setData(Resources.SessionStorageKeys.Email, (userEmail));
    this.sessionService.setData(Resources.SessionStorageKeys.MarketingConsentChannels, this.contactDetailsFormGroup?.get('consent')?.value || this.CtmMarketingConsent);
    this.sessionService.setData(Resources.SessionStorageKeys.IsConsentApproval, true);

    // Store into session storage
    this.sessionService.setData(Resources.SessionStorageKeys.SelectedAddress, this.selectedAddress);
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Postcode, this.postcode);

    if (userEmail) {
      this.googleAnalytics.track({ email: this.utilityService.getObfuscatedEmail(userEmail) }, 'email_consent_provided');
    }

    // Redirect to dashboard
    this.queryParams = { ...this.queryParams, correlationId: this.correlationId || '' }
    this.router.navigate(['/information'], {
      queryParams: new URLSearchParams(this.queryParams),
    });
  }

  // Navigate to quote
  navigateToQuote(): void {
    // Set the email in local storage
    const userEmail = this.contactDetailsFormGroup?.get('email')?.value?.trim()?.toLowerCase();
    this.postcode = this.selectedAddress?.addressPostcode || this.selectedAddress?.postcode || '';
    this.sessionService.setData(Resources.SessionStorageKeys.Email, (userEmail));
    this.sessionService.setData(Resources.SessionStorageKeys.MarketingConsentChannels, this.contactDetailsFormGroup?.get('consent')?.value || this.CtmMarketingConsent);
    this.sessionService.setData(Resources.SessionStorageKeys.IsConsentApproval, true);

    // Store into session storage
    this.sessionService.setData(Resources.SessionStorageKeys.SelectedAddress, this.selectedAddress);
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Postcode, this.postcode);

    // Re-Initialise presence socket to update the user name in all sessions
    this.socketService.updateUserName();

    if (userEmail) {
      this.googleAnalytics.track({ email: this.utilityService.getObfuscatedEmail(userEmail) }, 'email_consent_provided');
    }

    this.appService.updateApplicationStatus('update');

    // Redirect to energy result page with correlationId
    this.router.navigate(['/energy-result'], {
      queryParams: { correlationId: this.correlationId }
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}